import React, { useEffect, useRef, useState } from 'react';
import { Sparkles, TrendingUp, Gift } from 'lucide-react';
import confetti from 'canvas-confetti';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';

/**
 * Barra de progreso hacia el próximo tier de descuento por objetivo de carrito.
 * - Se muestra sólo si el user es elegible (audience) y hay tiers configurados.
 * - Dispara confeti la primera vez que el carrito CRUZA un tier alcanzado
 *   durante la sesión.
 * - Cuando cruza un tier, además del confeti muestra un banner desplegable
 *   "¡Felicidades! Conseguiste un X% de descuento" que se autocierra en ~3.5s
 *   y deja ver la barra actualizada apuntando al SIGUIENTE objetivo.
 * - Si algún tier tiene producto de regalo (Sistema de Premios por Volumen),
 *   muestra la miniatura + mensaje "Te faltan $X para llevarte este [producto]".
 */
// Formato coherente con CartContext.formatPrice (`$ 182.880`) para que el
// monto de la barra sea byte-identical al Subtotal (precio de lista) que
// se muestra en el CartSidebar.
const fmt = (n) => `$ ${Math.round(n || 0).toLocaleString('es-AR')}`;

const CartDiscountProgressBar = () => {
  const { cart, getRawCartTotal, selectedPaymentMethod } = useCart();
  const { user, storeSettings, getBestCartDiscountPct, getNextCartDiscountTier, getReachedGiftTier, getNextGiftTier } = useAuth();
  const lastBestPctRef = useRef(0);
  // ⛳ Ref con el último threshold cruzado.
  const lastCrossedThresholdRef = useRef(0);
  // 🌟 Flash "gold" cuando se cruza un tier (~1.4s).
  const [goldFlash, setGoldFlash] = useState(false);
  // ⚡ Shake cuando cambia la cantidad del carrito (feedback inmediato).
  const [shake, setShake] = useState(false);
  const lastCartCountRef = useRef(0);

  const tiers = Array.isArray(storeSettings?.cartDiscountTiers) ? storeSettings.cartDiscountTiers : [];
  const rawTotal = typeof getRawCartTotal === 'function' ? getRawCartTotal() : 0;
  // Si el método de pago actual bloquea descuentos por volumen igualmente
  // mostramos la barra: sirve de INCENTIVO para que el cliente sepa que puede
  // cambiar la forma de pago y acceder al descuento. Solo agregamos una nota
  // aclaratoria al pie de la barra (ver `pmBlocks` abajo).
  const pmBlocks = selectedPaymentMethod && selectedPaymentMethod.allowVolumeDiscounts === false;
  const bestPct = getBestCartDiscountPct ? getBestCartDiscountPct(rawTotal) : 0;
  const nextTier = getNextCartDiscountTier ? getNextCartDiscountTier(rawTotal) : null;
  const reachedGift = getReachedGiftTier ? getReachedGiftTier(rawTotal) : null;
  const nextGift = getNextGiftTier ? getNextGiftTier(rawTotal) : null;
  // Descuento permanente del cliente (winner-takes-all vs cartTier).
  const permPct = Number(user?.permanentDiscountPct || 0);
  // % que realmente se aplica en el catálogo/checkout.
  const winnerPct = Math.max(permPct, bestPct);
  // Si gana el permanente sobre el tier alcanzado, el banner debe reflejarlo.
  const winnerIsPermanent = permPct >= bestPct && permPct > 0;

  // Disparar confeti al cruzar un tier hacia arriba.
  useEffect(() => {
    const prev = lastBestPctRef.current;
    if (bestPct > prev && bestPct > 0) {
      // Confeti festivo pero corto (no bloquea).
      try {
        confetti({
          particleCount: 120,
          spread: 75,
          startVelocity: 45,
          origin: { x: 0.5, y: 0.35 },
          colors: ['#EC4899', '#06B6D4', '#FB923C', '#F59E0B', '#22C55E'],
          scalar: 0.9,
          disableForReducedMotion: true,
        });
        setTimeout(() => {
          try {
            confetti({
              particleCount: 60,
              spread: 60,
              angle: 60,
              origin: { x: 0.05, y: 0.6 },
              colors: ['#EC4899', '#06B6D4', '#FB923C'],
              disableForReducedMotion: true,
            });
            confetti({
              particleCount: 60,
              spread: 60,
              angle: 120,
              origin: { x: 0.95, y: 0.6 },
              colors: ['#EC4899', '#06B6D4', '#FB923C'],
              disableForReducedMotion: true,
            });
          } catch (e) { /* ignore */ }
        }, 250);
      } catch (e) { /* ignore */ }
    }
    lastBestPctRef.current = bestPct;
  }, [bestPct]);

  // 🎉 Detectar cruce de threshold: confeti + flash gold, SIN banner verde.
  useEffect(() => {
    if (tiers.length === 0) return;
    const sortedTiers = tiers
      .filter(t => Number(t?.threshold || 0) > 0 && Number(t?.pct || 0) > 0)
      .map(t => ({ ...t, threshold: Number(t.threshold || 0), pct: Number(t.pct || 0) }))
      .sort((a, b) => a.threshold - b.threshold);
    const justCrossed = sortedTiers
      .filter(t => t.threshold > lastCrossedThresholdRef.current && rawTotal >= t.threshold);
    if (justCrossed.length === 0) return;
    const top = justCrossed[justCrossed.length - 1];
    lastCrossedThresholdRef.current = top.threshold;
    // Flash dorado breve en la barra.
    setGoldFlash(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawTotal, tiers.length]);

  // Auto-dismiss del gold flash (~1.4s: legible pero corto).
  useEffect(() => {
    if (!goldFlash) return;
    const t = setTimeout(() => setGoldFlash(false), 1400);
    return () => clearTimeout(t);
  }, [goldFlash]);

  // ⚡ Feedback inmediato: sacudida al cambiar la CANTIDAD total de items
  // del carrito. Ignora la carga inicial.
  useEffect(() => {
    const now = cart.reduce((s, it) => s + (it.quantity || 0), 0);
    const prev = lastCartCountRef.current;
    lastCartCountRef.current = now;
    if (prev === 0) return;      // primer render — no shake
    if (now === prev) return;    // sin cambio real
    setShake(true);
    const t = setTimeout(() => setShake(false), 380);
    return () => clearTimeout(t);
  }, [cart]);

  // Cuando el carrito se vacía o baja por debajo del último cruzado, reseteamos
  // el "high water mark" para que el próximo cruce vuelva a celebrar.
  useEffect(() => {
    if (rawTotal === 0) lastCrossedThresholdRef.current = 0;
    else if (rawTotal < lastCrossedThresholdRef.current) lastCrossedThresholdRef.current = 0;
  }, [rawTotal]);

  if (tiers.length === 0) return null;
  // 🆕 La barra ahora se muestra SIEMPRE (aún con métodos que bloquean
  // descuentos por volumen). El objetivo es INCENTIVAR al cliente a
  // cambiar la forma de pago si quiere acceder al descuento.
  // Sin próximo tier y sin descuento activo → user no elegible o config vacía.
  if (!nextTier && bestPct === 0 && !nextGift && !reachedGift) return null;

  // Caso 1: Ya alcanzó el máximo tier disponible.
  if (!nextTier && bestPct > 0) {
    // Si el permanente le gana al cart-tier, informar el % REAL aplicado.
    const activePct = winnerIsPermanent ? permPct : bestPct;
    const giftProduct = reachedGift?.giftProduct;
    // 🎨 Cuando el método bloquea descuentos por volumen, evitamos el
    // "máximo desbloqueado" verde (contradice la nota amber). Usamos la
    // misma paleta neutra ámbar del caso "progreso" para ser coherentes.
    const wrapperClass = pmBlocks
      ? 'rounded-lg border border-amber-300 bg-gradient-to-r from-amber-50/95 via-yellow-50/95 to-orange-50/95 backdrop-blur-md px-2 py-1.5 shadow'
      : 'rounded-lg border border-emerald-300 bg-gradient-to-r from-emerald-50/95 via-teal-50/95 to-cyan-50/95 backdrop-blur-md px-2 py-1.5 shadow';
    const iconColor = pmBlocks ? 'text-amber-600' : 'text-emerald-600';
    const headlineColor = pmBlocks ? 'text-amber-900' : 'text-emerald-900';
    const pctColor = pmBlocks ? 'text-amber-700' : 'text-emerald-700';
    const chipClass = pmBlocks
      ? 'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0 text-[9px] font-bold whitespace-nowrap border bg-white text-amber-700 border-amber-300'
      : 'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0 text-[9px] font-bold whitespace-nowrap border bg-emerald-500 text-white border-emerald-600';
    return (
      <div className="w-full bg-white/95 backdrop-blur-lg border-b border-orange-100">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-1">
          <div
            className={wrapperClass}
            data-testid="cart-discount-progress-max"
          >
        <div className="flex items-center gap-2 min-w-0">
          {giftProduct?.imageUrl ? (
            <div className="relative flex-shrink-0" data-testid="cart-discount-gift-image-max">
              <img
                src={giftProduct.imageUrl}
                alt={giftProduct.name}
                className="w-7 h-7 object-contain rounded bg-white border border-emerald-400 shadow-sm"
              />
              <span className="absolute -top-1 -right-1 text-xs" aria-hidden>🎁</span>
            </div>
          ) : (
            <Sparkles className={`${iconColor} flex-shrink-0`} size={14} />
          )}
          <p className={`text-[10px] sm:text-xs font-bold ${headlineColor} flex-1 min-w-0 leading-tight truncate`} data-testid="cart-discount-headline">
            {giftProduct
              ? <>{pmBlocks ? 'Podrías llevarte' : 'Te llevás'} <span data-testid="cart-discount-gift-name">{giftProduct.name}</span> 🎁</>
              : <>{pmBlocks ? 'Máximo disponible' : 'Máximo desbloqueado'}</>
            }
            <span className={`ml-1 font-semibold ${pctColor}`}>
              {' · '}<span data-testid="cart-discount-active-pct">{activePct}%</span> off
            </span>
          </p>
        </div>
        {/* Roadmap inline (todos alcanzados) */}
        <div className="flex items-center gap-1 mt-1 overflow-x-auto" data-testid="cart-discount-tier-roadmap-max">
          {[...tiers]
            .filter(t => Number(t?.threshold || 0) > 0 && Number(t?.pct || 0) > 0)
            .sort((a, b) => Number(a.threshold) - Number(b.threshold))
            .map((t, idx) => {
              const th = Number(t.threshold || 0);
              const p = Number(t.pct || 0);
              return (
                <span key={idx} className={chipClass} data-testid={`cart-discount-tier-chip-max-${idx}`}>
                  <span aria-hidden>{pmBlocks ? '·' : '✓'}</span>{`> ${fmt(th)}: ${p}%`}
                </span>
              );
            })}
        </div>
        {pmBlocks && (
          <p className="text-[9px] text-amber-700 mt-1 leading-tight" data-testid="cart-discount-pm-blocks-note-max">
            ⚠ El método de pago actual (<b>{selectedPaymentMethod?.label || 'seleccionado'}</b>) no aplica descuentos por volumen. Cambiá a un método sin restricciones para acceder al descuento.
          </p>
        )}
        </div>
      </div>
    </div>
    );
  }

  // Caso 2: Progreso hacia el próximo tier (descuento y/o regalo).
  // Priorizamos mostrar el próximo GIFT si existe. Si no hay gift pero sí tier
  // de descuento simple, mostramos el descuento sin gift.
  const targetGift = nextGift?.giftProduct || null;
  const targetTier = nextGift || nextTier;
  if (!targetTier) return null;
  const pct = Math.max(0, Math.min(100, (rawTotal / Number(targetTier.threshold || 1)) * 100));
  const missing = Number(targetTier.threshold || 0) - rawTotal;
  const nextPct = Number(targetTier.pct || 0);
  // 🆕 Cantidad de niveles superiores más allá del actual (para tentar
  // al cliente con "hay más beneficios si sumás volumen"). Excluye el
  // tier objetivo actual.
  const higherLevels = tiers.filter(t => Number(t?.threshold || 0) > Number(targetTier?.threshold || 0));
  const maxAvailablePct = tiers.reduce((mx, t) => Math.max(mx, Number(t?.pct || 0)), 0);
  const isEmptyCart = cart.length === 0;

  return (
    <div className="w-full bg-white/95 backdrop-blur-lg border-b border-orange-100">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-1">
        <div
          className={`relative rounded-lg border bg-gradient-to-r from-orange-50/95 via-amber-50/95 to-yellow-50/95 backdrop-blur-md px-2 py-1.5 shadow ${shake ? 'animate-[cartShake_380ms_ease-out]' : ''} ${goldFlash ? 'animate-[goldFlash_1400ms_ease-out] border-yellow-400 shadow-yellow-300/60' : 'border-orange-200'}`}
          data-testid="cart-discount-progress"
        >
      {/* Banner verde de "level up" ELIMINADO por decisión de diseño.
          El feedback de cruce ahora es confeti + flash gold del borde
          (ver goldFlash state arriba) — más "videojuego" y menos invasivo. */}
      <div className="flex items-center gap-2">
        {targetGift?.imageUrl ? (
          <div className="relative flex-shrink-0" data-testid="cart-discount-gift-image">
            <img
              src={targetGift.imageUrl}
              alt={targetGift.name}
              className="w-7 h-7 object-contain rounded bg-white border border-orange-400 shadow-sm"
            />
            <span className="absolute -top-1 -right-1 text-xs" aria-hidden>🎁</span>
          </div>
        ) : (
          <TrendingUp className="text-orange-600 flex-shrink-0" size={14} />
        )}
        <p className="text-[10px] sm:text-xs font-bold text-orange-900 flex-1 min-w-0 leading-tight truncate" data-testid="cart-discount-headline">
          {winnerPct > 0 ? (
            <>{pmBlocks ? 'Podés obtener' : 'Ya tenés'} <span data-testid="cart-discount-active-pct">{winnerPct}%</span> off · </>
          ) : null}
          {isEmptyCart ? (
            (() => {
              const firstTier = nextTier || targetTier;
              const firstThreshold = Number(firstTier?.threshold || 0);
              const firstPct = Number(firstTier?.pct || 0);
              return (
                <>Sumá <span className="font-extrabold" data-testid="cart-discount-missing">{fmt(firstThreshold)}</span>{firstPct > 0 ? <> por <span className="font-extrabold">{firstPct}%</span></> : null}{targetGift ? <> + <span className="font-extrabold underline decoration-orange-400 decoration-2" data-testid="cart-discount-gift-name">{targetGift.name}</span> 🎁</> : null}</>
              );
            })()
          ) : targetGift ? (
            <>Faltan <span className="font-extrabold" data-testid="cart-discount-missing">{fmt(missing)}</span> para <span className="font-extrabold underline decoration-orange-400 decoration-2" data-testid="cart-discount-gift-name">{targetGift.name}</span> 🎁</>
          ) : (
            <>A <span className="font-extrabold" data-testid="cart-discount-missing">{fmt(missing)}</span> de {Math.max(nextPct, permPct)}% off</>
          )}
        </p>
      </div>
      <div className="w-full h-1.5 bg-white rounded-full overflow-hidden border border-orange-200 shadow-inner mt-1 relative">
        <div
          className="h-full rounded-full transition-all duration-500 ease-out relative overflow-hidden"
          style={{
            width: `${pct}%`,
            backgroundImage: goldFlash
              ? 'linear-gradient(90deg, #FDE047 0%, #FACC15 50%, #F59E0B 100%)'
              : 'linear-gradient(90deg, #FB923C 0%, #F59E0B 50%, #EC4899 100%)',
            backgroundSize: '200% 100%',
            animation: 'barShine 2200ms linear infinite',
          }}
          data-testid="cart-discount-progress-bar"
        />
      </div>
      {/* Fila combinada: totales a la izquierda + roadmap chips a la derecha
          (misma línea) para ahorrar altura. */}
      <div className="flex items-center gap-2 mt-1 min-w-0">
        <span className="text-[9px] text-orange-800 tabular-nums flex-shrink-0" data-testid="cart-discount-current-total">
          {fmt(rawTotal)}<span className="text-orange-500">/{fmt(Number(targetTier.threshold || 0))}</span>
        </span>
        {tiers.length > 0 && (
          <div className="flex items-center gap-1 overflow-x-auto flex-1 min-w-0" data-testid="cart-discount-tier-roadmap">
            {[...tiers]
              .filter(t => Number(t?.threshold || 0) > 0 && Number(t?.pct || 0) > 0)
              .sort((a, b) => Number(a.threshold) - Number(b.threshold))
              .map((t, idx) => {
                const th = Number(t.threshold || 0);
                const p = Number(t.pct || 0);
                const reached = rawTotal >= th;
                const isTarget = th === Number(targetTier?.threshold || 0);
                return (
                  <span
                    key={idx}
                    className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0 text-[9px] font-bold whitespace-nowrap border ${
                      reached
                        ? 'bg-emerald-500 text-white border-emerald-600'
                        : isTarget
                          ? 'bg-white text-orange-700 border-orange-400 ring-1 ring-orange-200'
                          : 'bg-white/60 text-orange-700 border-orange-200'
                    }`}
                    data-testid={`cart-discount-tier-chip-${idx}`}
                  >
                    {reached && <span aria-hidden>✓</span>}
                    {`> ${fmt(th)}: ${p}%`}
                  </span>
                );
              })}
          </div>
        )}
        </div>
        {pmBlocks && (
          <p className="text-[9px] text-amber-700 mt-1 leading-tight" data-testid="cart-discount-pm-blocks-note">
            ⚠ El método de pago actual (<b>{selectedPaymentMethod?.label || 'seleccionado'}</b>) no aplica descuentos por volumen. Cambiá a un método sin restricciones para acceder al descuento.
          </p>
        )}
        </div>
      </div>
    </div>
  );
};

export default CartDiscountProgressBar;
